import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Button,
  Box,
  Paper,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  OutlinedInput,
  InputAdornment,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { DataGrid, getGridSingleSelectOperators } from "@mui/x-data-grid";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import Popover from "@mui/material/Popover";
import { format } from "date-fns";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import SnackBar from "components/SnackBar/SnackBar";
import BackdropComponent from "components/Spinner/Spinner";
import exportAsCsv from "utils/exportAsCsv";
import {
  getClassTraineeMilestoneDetails,
  getMilestoneByRosterId,
  updateClassRosterCurriculumStatus,
  getCurriculumStatus,
  getTraineeStatusList,
  getClassRosterDetails,
  getSearchPopoverContent,
  updateClassTraineeMilestoneGridDetails,
  getSearchValue,
} from "../../../services/learnPointUserServices";
import classes from "../ClassRoster/LearnPointClassRoster.module.css";

import {
  LimitChip,
  StyledChip,
} from "components/IdmCredentialTracking/NoRosterIdCreateUpdateIdmRecord";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useAuth } from "context/AuthContext";

const CustomNoRowsOverlay = () => (
  <div className={classes.noRecordsOverlay}>
    <h3>No results to display.</h3>
  </div>
);

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .MuiTooltip-tooltip`]: {
    whiteSpace: "normal",
    fontSize: "0.85rem", // Adjust the font size as needed
    maxWidth: "150px", // Set a max width to ensure wrapping
  },
});

const PaperComponentTemplate = (props) => <Paper {...props} />;

const ItemHeading = styled(Paper)((props) => ({
  textAlign: "center",
  boxShadow: "none",
  fontWeight: 600,
  ...(props?.disabled && { paddingTop: 12 }),
}));

ItemHeading.propTypes = {
  disabled: PropTypes.bool,
};

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const RosterGrid = ({ classRosterId }) => {
  const { roles } = useAuth();

  const [btnDisabled, setBtnDisabled] = useState(true);
  const [openPopover, setOpenPopover] = useState(false);
  const [popoverContent, setPopoverContent] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);

  const [curriculumStatus, setCurriculumStatus] = useState([]);
  const [curriculumValue, setCurriculumValue] = useState({
    LP_CurriculumStatusId: null,
    CurriculumStatus: "",
  });
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [classRosterDetails, setClassRosterDetails] = useState([]);
  const [classRequestDetails, setClassRequestDetails] = useState([]);
  const [opsTrainerShow, setOpsTrainerShow] = useState(true);
  const [searchOpsLead, setSearchOpsLead] = useState([]);
  const [currentEditableRow, setCurrentEditableRow] = useState([]);
  const [refreshGrid, setRefreshGrid] = useState(false);
  const [statusUpdated, setStatusUpdated] = useState(null);
  const [error, setError] = useState(false);
  const [activeEditTrainee, setActiveEditTrainee] = useState(null);
  const [hoveredTrainee, setHoveredTrainee] = useState(null);
  const hoverLeaveTimer = useRef(null);

  const handleCellEditStart = (params) => {
    setActiveEditTrainee({
      oracleId: params.row.TraineeOracleId,
      traineeName: params.row.TraineeName,
    });
  };

  const handleCellEditStop = (params, event) => {
    const reason = params?.reason;
    if (
      reason === "escapeKeyDown" ||
      reason === "enterKeyDown" ||
      reason === "tabKeyDown"
    ) {
      setActiveEditTrainee(null);
    }
  };

  const handleRowMouseEnter = (params) => {
    // Cancel any pending leave — user re-entered a row quickly
    if (hoverLeaveTimer.current) {
      clearTimeout(hoverLeaveTimer.current);
      hoverLeaveTimer.current = null;
    }

    if (!activeEditTrainee) {
      setHoveredTrainee({
        oracleId: params.row.TraineeOracleId,
        traineeName: params.row.TraineeName,
      });
    }
  };

  const handleRowMouseLeave = (e) => {
    // If mouse is moving into the grid header, don't clear the banner
    // relatedTarget is the element the mouse is moving INTO
    const movingIntoHeader = e?.relatedTarget?.closest?.(
      ".MuiDataGrid-columnHeaders",
    );
    if (movingIntoHeader) return;

    // Delay the clear — if mouse re-enters a row within 80ms, cancel it
    hoverLeaveTimer.current = setTimeout(() => {
      setHoveredTrainee(null);
      hoverLeaveTimer.current = null;
    }, 80);
  };

  useEffect(() => {
    return () => {
      if (hoverLeaveTimer.current) {
        clearTimeout(hoverLeaveTimer.current);
      }
    };
  }, []);

  const initialFilterState = {
    items: [
      {
        columnField: "TraineeStatus",
        operatorValue: "is",
        value: "Active",
      },
    ],
  };

  const [filterState, setFilterState] = useState(initialFilterState);

  const handleFilterModelChange = (newModel) => {
    if (newModel.items.length === 0) {
      newModel.items.push({
        columnField: "TraineeStatus",
        operatorValue: "is",
        value: "Active",
      });
    }
    setFilterState({ items: newModel.items });
  };

  const [traineeOptions, setTraineeOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchTraineeOptions = async () => {
      try {
        const traineeStatusOption = await fetchTraineeStatus();

        if (!cancelled && traineeStatusOption) {
          setTraineeOptions(traineeStatusOption);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch trainee status:", error);
        }
      }
    };

    fetchTraineeOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  const [sortState, setSortState] = useState([
    {
      field: "TraineeOracleId",
      sort: "asc",
    },
  ]);

  const handleSortModelChange = (newSortModel) => {
    if (newSortModel.length !== sortState.length) {
      setSortState(newSortModel);
      return;
    }
    newSortModel.forEach((element, index) => {
      if (
        element.field !== sortState[index].field ||
        element.sort !== sortState[index].sort
      ) {
        setSortState(newSortModel);
      }
    });
  };

  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const handleCurriculumStatusChange = async (event, value) => {
    try {
      setLoading(true);
      const response = await updateClassRosterCurriculumStatus(
        classRosterId,
        value.LP_CurriculumStatusId,
      );
      if (response.message === "Success") {
        setCurriculumValue({
          LP_CurriculumStatusId: value.LP_CurriculumStatusId,
          CurriculumStatus: value.CurriculumStatus,
        });
        setLoading(false);
        setSnack({
          open: true,
          message: "Curriculum Status has been successfully updated.",
          severity: "success",
        });
      } else {
        setLoading(false);
        setSnack({
          open: true,
          message:
            "Failed to update curriculum status, please contact administrator.",
          severity: "error",
        });
      }
    } catch (error) {
      setLoading(false);
      setSnack({
        open: true,
        message: "Some internal error occured.",
        severity: "error",
      });
    }
  };

  const handleOnCellClick = async (params, event) => {
    if (
      activeEditTrainee &&
      params.row.TraineeOracleId !== activeEditTrainee.oracleId
    ) {
      setActiveEditTrainee(null);
    }
    if (["TraineeOracleId", "TraineeName"].includes(params.field.toString())) {
      setPopoverContent("");
      if (params.row["HasThirdPartyUpdate"]) {
        const Contentvalue = await getSearchPopoverContent(
          params.row["ClassTraineeId"],
        );
        if (Contentvalue.message === "Success") {
          setPopoverContent(Contentvalue.data[0]["TrackingDetails"]);
          setOpenPopover(true);
          setAnchorEl(event.target);
        }
      }
    }
  };

  const handleOnCellDoubleClick = (params) => {
    if (params.field.toString() === "OperationLeadName") {
      setCurrentEditableRow(params.row);
    }
  };

  const handleClose = () => {
    setOpenPopover(false);
    setAnchorEl(null);
  };

  const getTodayDate = () => {
    return new Date();
  };
  const getOneYearAgoDate = () => {
    const today = getTodayDate();
    return new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
  };

  const oneYearAgo = getOneYearAgoDate();

  const prepareUpdateValue = (value) => {
    if (value === null || value === undefined || value === "") return "null";
    if (typeof value === "string" && value.trim() === "") return "null";
    if (Array?.isArray(value)) {
      return value.length > 0 ? value.join(",") : "multiSelectNoValue";
    }

    return value?.toString(); // For other fields
  };

  // Helper function to check if field should be processed
  const shouldProcessField = (field) => {
    return field !== "OperationLeadName" && field !== "TraineeStatus";
  };

  // Helper function to check if value has changed
  const hasValueChanged = (changedRow, field, value) => {
    return changedRow[field] !== value;
  };

  // Helper function to check if field is a date field
  const isDateField = (field, columns) => {
    return (
      field === "ProductionDate" ||
      columns.find((col) => col.field === field)?.type === "date"
    );
  };

  // Helper function to format date value
  const formatDateValue = (value) => {
    if (value === "" || value === null) {
      return null;
    }
    return format(new Date(value), "MM/dd/yyyy");
  };

  // Helper function to get updated value based on field type
  const getUpdatedValue = (field, value, initialValue, columns) => {
    if (isDateField(field, columns)) {
      return formatDateValue(value);
    }

    if (value === null && initialValue) {
      return null;
    }

    return value;
  };

  // Helper function to process rows from API response
  const processRowsFromResponse = (updateClassTraineeGrid) => {
    const rowsData = Object.values(updateClassTraineeGrid.data)?.map(
      (item) => ({
        ...item,
        id: item.ClassTraineeId,
      }),
    );

    return rowsData.filter(
      (column) => column["TraineeStatus"]?.toLowerCase() !== "added in error",
    );
  };

  // Helper function to handle successful update
  const handleSuccessfulUpdate = (
    updateClassTraineeGrid,
    setRows,
    setLoading,
    setSnack,
  ) => {
    const processedRows = processRowsFromResponse(updateClassTraineeGrid);
    setRows(processedRows);
    setLoading(false);
    setActiveEditTrainee(null);

    setSnack({
      open: true,
      message: "Record has been successfully updated.",
      severity: "success",
    });
  };

  // Helper function to handle failed update
  const handleFailedUpdate = (setLoading, setSnack) => {
    setLoading(false);
    setSnack({
      open: true,
      message: "Encounter error while updating changes.",
      severity: "error",
    });
  };

  // Helper function to handle no changes detected
  const handleNoChanges = (setLoading, setSnack) => {
    setLoading(false);
    setSnack({
      open: true,
      message: "No changes detected.",
      severity: "info",
    });
  };

  // Helper function to perform the update operation
  const performUpdate = async (
    classRosterId,
    id,
    field,
    updatedValue,
    prepareUpdateValue,
    setLoading,
    setRows,
    setSnack,
  ) => {
    setLoading(true);

    const updateClassTraineeGrid = await updateClassTraineeMilestoneGridDetails(
      classRosterId,
      id,
      field,
      prepareUpdateValue(updatedValue),
    );

    if (updateClassTraineeGrid?.message === "Success") {
      handleSuccessfulUpdate(
        updateClassTraineeGrid,
        setRows,
        setLoading,
        setSnack,
      );
    } else {
      handleFailedUpdate(setLoading, setSnack);
    }
  };

  // Refactored main function
  const handleCellEditCommit = async ({ id, field, value }) => {
    try {
      if (error) {
        setSnack({
          open: true,
          message: "Invalid dates couldn't be saved",
          severity: "error",
        });
        return;
      }

      setOpsTrainerShow(true);

      if (!shouldProcessField(field)) {
        return;
      }

      const changedRow = rows.find((row) => row.id === id);

      if (!hasValueChanged(changedRow, field, value)) {
        handleNoChanges(setLoading, setSnack);
        return;
      }

      const updatedValue = getUpdatedValue(
        field,
        value,
        changedRow[field],
        columns,
      );

      await performUpdate(
        classRosterId,
        id,
        field,
        updatedValue,
        prepareUpdateValue,
        setLoading,
        setRows,
        setSnack,
      );
    } catch (error) {
      setLoading(false);
      setSnack({
        open: true,
        message: "Some internal error occured",
        severity: "error",
      });
    }
  };

  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const searchHandler = useCallback(
    debounce(async (opsLead) => {
      try {
        setLoading(true);
        if (opsLead && opsLead.length > 2) {
          const valueSearch = await getSearchValue(opsLead);
          if (valueSearch.message === "Success") {
            setSearchOpsLead(
              valueSearch.data.filter((item) => item.Email.length > 0),
            );
            setOpsTrainerShow(false);
            setSnack({
              open: true,
              message: "We've found a matching result for your query",
              severity: "success",
            });
          } else {
            setSnack({
              open: true,
              message: "Couldn't find any results matching your query.",
              severity: "error",
            });
          }
        } else {
          setSnack({
            open: true,
            message: "Please enter at least 3 characters.",
            severity: "error",
          });
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setSnack({
          open: true,
          message: "Some internal error occurred.",
          severity: "error",
        });
      }
    }, 300),
    [],
  );

  const handleOpsLeadChange = async (values, gridApi, cellId, cellField) => {
    try {
      if (
        currentEditableRow["OperationalTeamLeadId"] !== values.OracleId &&
        values !== null
      ) {
        setRows((prev) =>
          prev.map((item) =>
            item.id === currentEditableRow["id"]
              ? {
                  ...item,
                  OperationLeadName: `${values.Name} (${values.Email})`,
                  OperationalTeamLeadId: values.OracleId,
                }
              : item,
          ),
        );

        setSearchOpsLead([]);
        setOpsTrainerShow(true);

        gridApi.setCellMode(cellId, cellField, "view");

        setLoading(true);

        const updateClassTraineeGrid =
          await updateClassTraineeMilestoneGridDetails(
            classRosterId,
            currentEditableRow["id"],
            "OperationLeadName",
            prepareUpdateValue(values.OracleId),
          );

        setLoading(false);

        if (updateClassTraineeGrid?.message === "NotFound") {
          setSnack({
            open: true,
            message: "Operation Lead has been successfully updated..",
            severity: "success",
          });
        } else {
          setRows((prev) =>
            prev.map((item) =>
              item.id === currentEditableRow["id"]
                ? {
                    ...item,
                    OperationLeadName: currentEditableRow["OperationLeadName"],
                    OperationalTeamLeadId:
                      currentEditableRow["OperationalTeamLeadId"],
                  }
                : item,
            ),
          );
          setSnack({
            open: true,
            message: "Encountered error while updating changes..",
            severity: "error",
          });
        }
      } else {
        setSnack({
          open: true,
          message: "No change detected.",
          severity: "info",
        });
      }
    } catch (error) {
      setLoading(false);
      setRows((prev) =>
        prev.map((item) =>
          item.id === currentEditableRow["id"]
            ? {
                ...item,
                OperationLeadName: currentEditableRow["OperationLeadName"],
                OperationalTeamLeadId:
                  currentEditableRow["OperationalTeamLeadId"],
              }
            : item,
        ),
      );
      setSnack({
        open: true,
        message: "Some internal error occurred..",
        severity: "error",
      });
    }
  };

  const handleTraineeStatusChange = async (key, values, oldvalue) => {
    try {
      if (oldvalue !== values.props.value && values !== null) {
        setLoading(true);
        const updateClassTraineeGrid =
          await updateClassTraineeMilestoneGridDetails(
            classRosterId,
            key,
            "TraineeStatus",
            prepareUpdateValue(values.props.value),
          );

        if (updateClassTraineeGrid?.message === "NotFound") {
          document.activeElement.blur();
          setRefreshGrid(true);
        } else {
          setLoading(false);
          setSnack({
            open: true,
            message: "Encounter error while updating changes.",
            severity: "error",
          });
        }
      } else {
        setLoading(false);
        setSnack({
          open: true,
          message: "No change detected.",
          severity: "info",
        });
      }
    } catch (error) {
      setLoading(false);
      setSnack({
        open: true,
        message: "Some internal error occured.",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        if (classRosterId && refreshGrid) {
          if (!cancelled) {
            setLoading(true);
            setBtnDisabled(true);
          }

          const traineeStatusOption = await fetchTraineeStatus();

          if (cancelled) return;

          const staticColumns = prepareStaticColumns(traineeStatusOption);
          await fetchAndPrepareDynamicColumns(staticColumns);

          if (cancelled) return;

          await fetchAndSetupClassRosterGridRows();

          if (!cancelled) {
            setLoading(false);
            setSnack({
              open: true,
              message: "Record has been successfully updated.",
              severity: "success",
            });
            setBtnDisabled(false);
            setRefreshGrid(false);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setLoading(false);
          setSnack({
            open: true,
            message: "Some internal error occurred.",
            severity: "error",
          });
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [refreshGrid, classRosterId]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        if (classRosterId) {
          if (!cancelled) {
            setLoading(true);
            setBtnDisabled(true);
          }

          const traineeStatusOption = await fetchTraineeStatus();

          if (cancelled) return;

          const staticColumns = prepareStaticColumns(traineeStatusOption);
          await fetchAndPrepareDynamicColumns(staticColumns);

          if (cancelled) return;

          await fetchAndSetupClassRosterGridRows();

          if (!cancelled) {
            setBtnDisabled(false);
            setLoading(false);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setLoading(false);
          setSnack({
            open: true,
            message: "Some internal error occurred.",
            severity: "error",
          });
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [opsTrainerShow, classRosterId]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        if (classRosterId) {
          if (!cancelled) {
            setLoading(true);
            setBtnDisabled(true);
          }

          const traineeStatusOption = await fetchTraineeStatus();

          if (cancelled) return;

          await fetchcurriculumStatus();

          if (cancelled) return;

          await fetchClassRosterDetails(classRosterId);

          if (cancelled) return;

          const staticColumns = prepareStaticColumns(traineeStatusOption);
          await fetchAndPrepareDynamicColumns(staticColumns);

          if (cancelled) return;

          await fetchAndSetupClassRosterGridRows();

          if (!cancelled) {
            setBtnDisabled(false);
            setLoading(false);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setLoading(false);
          setSnack({
            open: true,
            message: "Some internal error occurred.",
            severity: "error",
          });
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [classRosterId]);

  const prepareDynamicColumnsValueOptions = (columnItem) => {
    if (columnItem !== "NA") {
      return columnItem.split(",");
    }
    return null;
  };

  const fetchTraineeStatus = async () => {
    const ALLOWED_ACCESS_LEVELS = [
      "LD_Leader",
      "LD_Trainer",
      "LD_Group",
      "LD_Group_Admin",
      "Ops_Group",
    ];

    const checkUserAccessLevel = (accessLevel) => {
      return (
        ALLOWED_ACCESS_LEVELS.includes(accessLevel) &&
        roles.includes(accessLevel)
      );
    };

    try {
      const traineeStatus = await getTraineeStatusList();
      if (traineeStatus?.message === "Success") {
        const updatedStatus = traineeStatus.data.map((item) => {
          const label = item.label.toLowerCase();
          const isAdmin = checkUserAccessLevel("LD_Group_Admin");
          const isTrainer =
            checkUserAccessLevel("LD_Trainer") ||
            checkUserAccessLevel("LD_Group");
          const isLeader = checkUserAccessLevel("LD_Leader");
          const isOpsGroup = checkUserAccessLevel("Ops_Group");

          const disabled =
            (label === "active" && isAdmin) ||
            (label === "added in error" && isAdmin) ||
            (label === "pending r/t" &&
              (isAdmin || isOpsGroup || isTrainer || isLeader));

          return {
            ...item,
            disabled,
          };
        });

        return updatedStatus;
      }
    } catch (error) {
      setSnack({
        open: true,
        message: "Error fetching trainee status",
        severity: "error",
      });
    }
  };

  const fetchcurriculumStatus = async () => {
    const curriculumStatus = await getCurriculumStatus();
    if (curriculumStatus?.message === "Success") {
      setCurriculumStatus(curriculumStatus?.data);
    }
  };

  const validateAndGetOpsLeadName = (item, params, value) => {
    if (item.ClassTraineeId === params.row.ClassTraineeId)
      return {
        ...item,
        OperationLeadName: value,
      };
    else {
      return item;
    }
  };

  const fetchClassRosterDetails = async (classRosterId) => {
    const classRosterDetails = await getClassRosterDetails(classRosterId);
    if (classRosterDetails?.message === "Success") {
      setClassRosterDetails(classRosterDetails?.data);

      setClassRequestDetails(
        classRosterDetails?.data.lpClassRequestProjects[0],
      );

      if (classRosterDetails?.data.curriculumStatus !== null) {
        setCurriculumValue({
          LP_CurriculumStatusId: classRosterDetails?.data.curriculumStatusId,
          CurriculumStatus: classRosterDetails?.data.curriculumStatus,
        });
      }
    }
  };

  const getMultiSelectSelectedValues = (value) => {
    const values = Array.isArray(value) ? value : value?.split(",");
    return values;
  };

  const MultiSelectEditComponent = ({ id, value, field, api, colDef }) => {
    const handleChange = (event, newValue) => {
      api.setEditCellValue({ id, field, value: newValue }, event);
    };

    let selectedValues = getMultiSelectSelectedValues(value);

    return (
      <Autocomplete
        multiple
        fullWidth
        options={colDef.valueOptions || []}
        value={selectedValues}
        onChange={handleChange}
        renderInput={(params) => (
          <TextField {...params} placeholder="Select Status" />
        )}
        renderTags={(value, getTagProps) => {
          const limitTags = 3;
          const more = value.length - limitTags;
          return (
            <>
              {value.slice(0, limitTags).map((option, index) => (
                <StyledChip
                  label={`${option} `}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))}
              {more > 0 && <LimitChip label={`+${more}`} size="small" />}
            </>
          );
        }}
      />
    );
  };

  const MemoEditComponent = (props) => {
    const { id, value, field, api } = props;

    const handleChange = (event) => {
      api.setEditCellValue({ id, field, value: event.target.value }, event);
    };

    return (
      <TextField
        multiline
        minRows={1}
        maxRows={2}
        fullWidth
        value={value || ""}
        onChange={handleChange}
        sx={{
          "& .MuiInputBase-inputMultiline": {
            overflowY: "auto",
            maxHeight: "120px",
          },
          "& .MuiFormHelperText-root": {
            position: "absolute",
            bottom: 8,
            right: 8,
            color: value?.length >= 2000 ? "red" : "inherit",
          },
        }}
        inputProps={{
          maxLength: 2000,
        }}
        helperText={
          value?.length >= 2000
            ? "Maximum 2000 characters reached"
            : `${value?.length || 0}/2000`
        }
      />
    );
  };

  const DateEditComponet = (props) => {
    const { id, field, value, api } = props;

    const handleChange = (newValue) => {
      if (newValue === null) {
        setError(false);
        api.setEditCellValue({ id, field, value: "" }, { debonceMs: 200 });
      } else if (newValue?.isValid()) {
        const formatted = newValue.format("YYYY-MM-DD");
        setError(false);
        api.setEditCellValue(
          { id, field, value: formatted },
          { debonceMs: 200 },
        );
      } else {
        setError(true);
      }
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          clearable
          value={value ? dayjs(value) : null}
          onChange={handleChange}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              sx={{
                width: 150,
              }}
            />
          )}
        />
      </LocalizationProvider>
    );
  };

  // ============================================================================
  // EXTRACTED RENDER COMPONENTS
  // ============================================================================

  // Component for Oracle ID cell with info icon
  const OracleIdCell = ({ params }) => (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {params.row.HasThirdPartyUpdate && (
        <CustomTooltip
          title={
            <span>
              Click on <strong>Oracle Id</strong> OR{" "}
              <strong>Trainee Name</strong> value for more info.
            </span>
          }
          placement="bottom-end"
        >
          <InfoIcon sx={{ color: "#3299FF", marginRight: 1 }} />
        </CustomTooltip>
      )}
      {params.value}
    </Box>
  );

  // Component for Ops Lead input with search
  const OpsLeadInput = ({
    params,
    setRows,
    validateAndGetOpsLeadName,
    searchHandler,
    classes,
  }) => (
    <FormControl>
      <OutlinedInput
        id={`input-${params.row.ClassTraineeId}`}
        value={params?.row?.OperationLeadName}
        onChange={(event) => {
          setRows((prev) =>
            prev.map((item) =>
              validateAndGetOpsLeadName(item, params, event.target.value),
            ),
          );
        }}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              id={`icon-button-${params.row.ClassTraineeId}`}
              type="button"
              sx={{ p: "10px" }}
              aria-label="search"
              onClick={() => {
                searchHandler(params.row.OperationLeadName);
              }}
            >
              <SearchIcon />
            </IconButton>
          </InputAdornment>
        }
        label="Ops Lead"
      />
    </FormControl>
  );

  // Component for Ops Lead autocomplete
  const OpsLeadAutocomplete = ({
    params,
    searchOpsLead,
    handleOpsLeadChange,
    PaperComponentTemplate,
    classes,
    gridApi,
    cellId,
    cellField,
  }) => (
    <Autocomplete
      id={`autocomplete-${params.row.ClassTraineeId}`}
      disablePortal
      freeSolo={true}
      openOnFocus
      disableClearable={true}
      className={classes.searchField}
      options={searchOpsLead}
      getOptionLabel={(option) => `${option.Name} (${option.Email})`}
      renderInput={(params) => (
        <TextField {...params} label="Select Ops Lead" />
      )}
      onChange={(event, values) => {
        handleOpsLeadChange(values, gridApi, cellId, cellField);
      }}
      PaperComponent={PaperComponentTemplate}
    />
  );

  // Component for Operation Lead edit cell
  const OperationLeadEditCell = ({
    params,
    opsTrainerShow,
    setRows,
    validateAndGetOpsLeadName,
    searchHandler,
    searchOpsLead,
    handleOpsLeadChange,
    PaperComponentTemplate,
    classes,
    gridApi,
    cellId,
    cellField,
  }) => (
    <>
      {opsTrainerShow ? (
        <OpsLeadInput
          params={params}
          setRows={setRows}
          validateAndGetOpsLeadName={validateAndGetOpsLeadName}
          searchHandler={searchHandler}
          classes={classes}
        />
      ) : (
        <OpsLeadAutocomplete
          params={params}
          searchOpsLead={searchOpsLead}
          handleOpsLeadChange={handleOpsLeadChange}
          PaperComponentTemplate={PaperComponentTemplate}
          classes={classes}
          gridApi={gridApi}
          cellId={cellId}
          cellField={cellField}
        />
      )}
    </>
  );

  // Component for Trainee Status edit cell
  const TraineeStatusEditCell = ({
    params,
    statusUpdated,
    traineeStatusOption,
    handleTraineeStatusChange,
    setStatusUpdated,
  }) => {
    const isUpdated = statusUpdated === params.id;

    if (isUpdated) {
      return (
        <span>
          {traineeStatusOption.find(
            (option) => option.value === params.row.TraineeStatusId,
          )?.label || ""}
        </span>
      );
    }

    return (
      <Select
        sx={{
          backgroundColor: "#f0f0f0",
          borderRadius: "4px",
        }}
        value={params.row.TraineeStatusId}
        onChange={(_event, values) => {
          handleTraineeStatusChange(
            params.row.ClassTraineeId,
            values,
            params.row.TraineeStatusId,
          );
          setStatusUpdated(params.id);
        }}
        autoFocus
      >
        {traineeStatusOption.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={!option.disabled}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    );
  };

  // ============================================================================
  // MAIN COLUMN DEFINITION FUNCTION
  // ============================================================================

  const prepareStaticColumns = (traineeStatusOption) => [
    {
      field: "ClassTraineeId",
      headerName: "Trainee Id",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "number",
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
      flex: 1,
    },
    {
      field: "TraineeOracleId",
      headerName: "Oracle Id",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "number",
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
      renderCell: (params) => <OracleIdCell params={params} />,
    },
    {
      field: "TraineeName",
      headerName: "Trainee Name",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "string",
      valueGetter: (params) => {
        const [firstName, lastName] = params.value.split(" ");
        return `${lastName} ${firstName}`;
      },
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
      width: 200,
    },
    {
      field: "TraineeComment",
      headerName: "Comment",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "string",
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
    },
    {
      field: "EmployeeStatus",
      headerName: "Employee Status",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "string",
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
      width: 150,
    },
    {
      field: "TraineeStatus",
      headerName: "Trainee Status",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "singleSelect",
      valueOptions: traineeOptions.map((option) => `${option.label}`),
      filterOperators: getGridSingleSelectOperators().filter(
        (operator) => operator.value === "is",
      ),
      renderEditCell: (params) => (
        <TraineeStatusEditCell
          params={params}
          statusUpdated={statusUpdated}
          traineeStatusOption={traineeStatusOption}
          handleTraineeStatusChange={handleTraineeStatusChange}
          setStatusUpdated={setStatusUpdated}
        />
      ),
      valueGetter: (params) => {
        const status = traineeStatusOption.find(
          (option) => option.value === params.row.TraineeStatusId,
        );
        return status ? status.label : "";
      },
      hideable: false,
      sortable: true,
      editable: true,
      align: "center",
      width: 150,
    },
    {
      field: "FTRR",
      headerName: "FTRR",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "boolean",
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
      flex: 1,
    },
    {
      field: "EndorsedToNestingStatus",
      headerName: "E2N",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "boolean",
      hideable: false,
      sortable: true,
      editable: true,
      align: "center",
      flex: 1,
    },
    {
      field: "E2P",
      headerName: "E2P",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "boolean",
      hideable: false,
      sortable: true,
      editable: true,
      align: "center",
      flex: 1,
    },
    {
      field: "OperationalTeamLeadId",
      headerName: "Ops Lead Id",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "number",
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
    },
    {
      field: "OperationLeadName",
      headerName: "Operation Lead",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "string",
      renderEditCell: (params) => (
        <OperationLeadEditCell
          params={params}
          opsTrainerShow={opsTrainerShow}
          setRows={setRows}
          validateAndGetOpsLeadName={validateAndGetOpsLeadName}
          searchHandler={searchHandler}
          searchOpsLead={searchOpsLead}
          handleOpsLeadChange={handleOpsLeadChange}
          PaperComponentTemplate={PaperComponentTemplate}
          classes={classes}
          gridApi={params.api}
          cellId={params.id}
          cellField={params.field}
        />
      ),
      hideable: false,
      sortable: true,
      editable: true,
      align: "center",
      width: 250,
    },
    {
      field: "ProductionDate",
      headerName: "Production Date",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "date",
      renderCell: (params) => {
        format(new Date(params.row.ProductionDate), "MM/dd/yyyy");
      },
      valueFormatter: (params) => {
        return format(new Date(params.value), "MM/dd/yyyy");
      },
      valueGetter: (params) => {
        return format(new Date(params.value), "MM/dd/yyyy");
      },
      preProcessEditCellProps: (params) => {
        const dateValue = new Date(params.props.value);
        const hasError = dateValue < oneYearAgo;
        if (hasError) {
          setSnack({
            open: true,
            message: "Production Date cannot be older than one year.",
            severity: "error",
          });
        }
        return { ...params.props, error: hasError };
      },
      hideable: false,
      sortable: true,
      editable: true,
      align: "center",
    },
    {
      field: "Project",
      headerName: "Project",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "string",
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
      width: 200,
    },
    {
      field: "Site",
      headerName: "Site",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "string",
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
      width: 200,
    },
  ];

  const fetchAndPrepareDynamicColumns = async (staticColumns) => {
    const dynamicMilestoneList = await getMilestoneByRosterId(classRosterId);

    if (dynamicMilestoneList?.message === "Success") {
      const dynamicColumns = Object.keys(dynamicMilestoneList.data[0])?.map(
        (milestoneName) => {
          const milestoneValue = dynamicMilestoneList.data[0][milestoneName];
          const [rawType, rawOptions] = milestoneValue?.split("-");
          const type = rawType?.trim().toLowerCase();
          const options = rawOptions?.trim();

          return {
            field: milestoneName,
            headerName: milestoneName,
            headerClassName: classes.headerClass,
            headerAlign: "center",
            ...(options && {
              valueOptions: prepareDynamicColumnsValueOptions(options),
            }),
            ...(type !== "multiselect" && type !== "memo"
              ? { type: rawType }
              : {}),
            ...(type === "multiselect" && {
              renderEditCell: (params) => (
                <MultiSelectEditComponent {...params} />
              ),
              width: 400,
            }),
            ...(type === "memo" && {
              renderEditCell: (params) => <MemoEditComponent {...params} />,
              width: 400,
            }),
            ...(type === "date" && {
              renderEditCell: (params) => <DateEditComponet {...params} />,
              width: 200,
              type: "date",
              valueFormatter: (params) => {
                if (!params.value || params.value === "null") return "";
                try {
                  return format(new Date(params.value), "MM/dd/yyyy");
                } catch {
                  return "";
                }
              },
              valueGetter: (params) => {
                if (!params.value || params.value === "null") return "";
                return params.value;
              },
            }),
            align: "center",
            hideable: false,
            sortable: true,
            editable: true,
          };
        },
      );

      setColumns([...staticColumns, ...dynamicColumns]);
    } else {
      setColumns([...staticColumns]);
    }
  };

  const fetchAndSetupClassRosterGridRows = async () => {
    const classTraineeMilestoneGrid =
      await getClassTraineeMilestoneDetails(classRosterId);

    if (classTraineeMilestoneGrid?.message === "Success") {
      const rowsData = Object.values(classTraineeMilestoneGrid.data)?.map(
        (item) => {
          return { ...item, id: item.ClassTraineeId };
        },
      );
      setRows(
        rowsData.filter(
          (column) =>
            column["TraineeStatus"]?.toLowerCase() !== "added in error",
        ),
      );
    } else {
      setSnack({
        open: true,
        message: "Records not found for Roster Id",
        severity: "error",
      });
    }
  };
  const bannerTrainee = activeEditTrainee || hoveredTrainee;

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Grid
        container
        direction="row"
        justifyContent="space-evenly"
        alignItems="center"
        spacing={1}
      >
        <Grid item md={1}>
          <CustomButton
            variant="contained"
            sx={{ borderRadius: 0 }}
            startIcon={<DownloadIcon />}
            onClick={() => exportAsCsv(rows, columns, "LPClassRoster")}
            disabled={btnDisabled}
          >
            EXPORT
          </CustomButton>
        </Grid>
        <Grid item md={1.5}>
          <b>Class Roster:</b> {classRosterDetails.classRosterId}
        </Grid>
        <Grid item md="auto">
          <b>Class Name:</b> {classRosterDetails.className || "NA"}
        </Grid>
        <Grid item md="auto">
          <b>Project:</b> {classRequestDetails.project || "NA"}
        </Grid>
        <Grid item md="auto">
          <b>Curriculum Status:</b>
        </Grid>
        <Grid item md="auto">
          <Autocomplete
            disablePortal
            freeSolo
            openOnFocus
            sx={{
              width: 200,
              opacity: 1,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: "gray",
              borderRadius: 32,
            }}
            value={curriculumValue}
            disableClearable
            disabled={btnDisabled}
            options={curriculumStatus}
            getOptionLabel={(option) => `${option?.CurriculumStatus}`}
            onChange={(event, value) => {
              handleCurriculumStatusChange(event, value);
            }}
            className={classes.searchField}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 32,
                    border: "1px solid #d3d3d3",
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid item md={12} sx={{ display: "flex", flexDirection: "column" }}>
          {bannerTrainee && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 0.75,
                mb: 1,
                backgroundColor: activeEditTrainee ? "#e8f4ff" : "#f5f5f5",
                border: `1px solid ${activeEditTrainee ? "#3299FF" : "#bdbdbd"}`,
                borderRadius: "8px",
                fontFamily: "Urbanist, sans-serif",
                fontSize: "1rem",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.10)",
                transition:
                  "background-color 0.2s ease, border-color 0.2s ease",
              }}
            >
              <InfoIcon
                sx={{
                  fontSize: "1.1rem",
                  color: activeEditTrainee ? "#3299FF" : "#9e9e9e",
                }}
              />
              <span>
                {activeEditTrainee ? "Editing Trainee: " : "Trainee :"}
                <strong>{bannerTrainee.traineeName}</strong>
                <span
                  style={{
                    color: "#666",
                    marginLeft: 6,
                  }}
                >
                  Oracle ID: <strong>{bannerTrainee.oracleId}</strong>
                </span>
              </span>
            </Box>
          )}

          <Box className={classes.dataGridContainer} sx={{ flexGrow: 1 }}>
            <DataGrid
              sx={{
                height: "100%",
                "& .MuiDataGrid-columnHeaders": {
                  position: "sticky",
                  zIndex: 1000,
                },
                "& .MuiDataGrid-virtualScroller": {
                  marginTop: "0 !important",
                },
                "& .MuiDataGrid-main": {
                  overflow: "clip",
                  height: "100%",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#3299FF",
                },
                "& .MuiDataGrid-filterIcon": {
                  color: "#3299FF",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontFamily: "Urbanist, sans-serif",
                  fontWeight: "bold",
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "#f0f7ff",
                    cursor: "pointer",
                  },
                  // Stronger highlight when actively editing that row
                  "& .MuiDataGrid-row.Mui-selected": {
                    backgroundColor: "#daeeff",
                  },
                },
              }}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    ClassTraineeId: false,
                    OperationalTeamLeadId: false,
                  },
                },
              }}
              columns={columns}
              rows={rows}
              getRowId={(row) => row.id}
              pageSize={100}
              disableSelectionOnClick
              rowsPerPageOptions={[100]}
              components={{
                NoRowsOverlay: CustomNoRowsOverlay,
              }}
              sortModel={sortState}
              onSortModelChange={handleSortModelChange}
              filterModel={filterState}
              onFilterModelChange={handleFilterModelChange}
              onCellDoubleClick={handleOnCellDoubleClick}
              onCellEditCommit={handleCellEditCommit}
              editMode="cell"
              onCellEditStart={handleCellEditStart}
              onCellEditStop={handleCellEditStop}
              onCellClick={handleOnCellClick}
              componentsProps={{
                row: {
                  onMouseEnter: (e) => {
                    const rowId = Number(
                      e.currentTarget.getAttribute("data-id"),
                    );
                    const row = rows.find((r) => r.id === rowId);
                    if (row) handleRowMouseEnter({ row });
                  },
                  onMouseLeave: (e) => handleRowMouseLeave(e), // ← pass e
                },
              }}
            />
            {openPopover && anchorEl && (
              <Popover
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                open={openPopover}
                onClose={handleClose}
                disableRestoreFocus
              >
                <Box sx={{ p: 1.25, bgcolor: "background.paper" }}>
                  <p
                    className={classes.rowheight}
                    dangerouslySetInnerHTML={{ __html: popoverContent }}
                  ></p>
                </Box>
              </Popover>
            )}
          </Box>
        </Grid>
      </Grid>
      <SnackBar snack={snack} snackReset={snackReset} />
    </>
  );
};

RosterGrid.propTypes = {
  classRosterId: PropTypes.number,
};

export default RosterGrid;
